import {
  getGreetingCardRotatedBounds,
  greetingCardRectsOverlap,
  greetingCardSafeZonesToTemplateRects,
  greetingCardTransformOverlapsSafeZones,
  type GreetingCardDecorationTransform,
  type GreetingCardSafeZone,
  type LayoutRect,
} from '@giftie/shared/greeting-cards'
import type { YuzuPaperSize, YuzuTemplatePage } from '@giftie/yuzu-integration'
import type { Decoration } from '~/types'
import {
  MIN_QR_DECORATION_HEIGHT,
  MIN_QR_DECORATION_SIZE,
  QR_DECORATION_HEIGHT_SCALE,
} from './designConstants'
import { getYuzuPageCssDimensions } from './yuzuProtectedAreas'
import { getYuzuPageDimensions } from './yuzuTemplateLayout'

const CSS_PIXELS_PER_MILLIMETRE = 96 / 25.4
const BOUNDS_EPSILON = 0.001
const DEFAULT_DECORATION_INSET_PX = 16
export const DEFAULT_PAGE_EDGE_SAFE_AREA_MM = 5

type TemplateSize = {
  width: number
  height: number
}

export function resolvePageEdgeSafeAreaMm(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : DEFAULT_PAGE_EDGE_SAFE_AREA_MM
}

export function getPageEdgeSafeAreaRect(
  safeAreaMm: number,
  paperSize: YuzuPaperSize,
  orientation: YuzuTemplatePage['orientation'],
  template: TemplateSize,
): LayoutRect {
  const pageDimensions = getYuzuPageDimensions(paperSize, orientation)
  const pageCssDimensions = getYuzuPageCssDimensions({
    ...pageDimensions,
    units: paperSize.units,
  })
  const insetCssPx = Math.max(0, safeAreaMm) * CSS_PIXELS_PER_MILLIMETRE

  if (!pageCssDimensions) {
    return { x: 0, y: 0, width: template.width, height: template.height }
  }

  const insetX = Math.min(
    template.width / 2,
    (insetCssPx / pageCssDimensions.width) * template.width,
  )
  const insetY = Math.min(
    template.height / 2,
    (insetCssPx / pageCssDimensions.height) * template.height,
  )

  return {
    x: insetX,
    y: insetY,
    width: template.width - insetX * 2,
    height: template.height - insetY * 2,
  }
}

export function isDecorationInsideBounds(
  transform: GreetingCardDecorationTransform,
  bounds: LayoutRect,
) {
  const decorationBounds = getGreetingCardRotatedBounds(transform)
  return (
    decorationBounds.x >= bounds.x - BOUNDS_EPSILON
    && decorationBounds.y >= bounds.y - BOUNDS_EPSILON
    && decorationBounds.x + decorationBounds.width <= bounds.x + bounds.width + BOUNDS_EPSILON
    && decorationBounds.y + decorationBounds.height <= bounds.y + bounds.height + BOUNDS_EPSILON
  )
}

export function constrainDecorationPosition(
  transform: GreetingCardDecorationTransform,
  bounds: LayoutRect,
): GreetingCardDecorationTransform | null {
  const decorationBounds = getGreetingCardRotatedBounds(transform)
  if (
    decorationBounds.width > bounds.width + BOUNDS_EPSILON
    || decorationBounds.height > bounds.height + BOUNDS_EPSILON
  ) {
    return null
  }

  let offsetX = 0
  let offsetY = 0
  if (decorationBounds.x < bounds.x) {
    offsetX = bounds.x - decorationBounds.x
  } else if (decorationBounds.x + decorationBounds.width > bounds.x + bounds.width) {
    offsetX = bounds.x + bounds.width - decorationBounds.x - decorationBounds.width
  }
  if (decorationBounds.y < bounds.y) {
    offsetY = bounds.y - decorationBounds.y
  } else if (decorationBounds.y + decorationBounds.height > bounds.y + bounds.height) {
    offsetY = bounds.y + bounds.height - decorationBounds.y - decorationBounds.height
  }

  return {
    ...transform,
    x: transform.x + offsetX,
    y: transform.y + offsetY,
  }
}

export function createDefaultQrRect(
  templateWidth: number,
  templateHeight: number,
  safeZones?: GreetingCardSafeZone[],
  existingDecorations: Decoration[] = [],
  contentBounds: LayoutRect = {
    x: 0,
    y: 0,
    width: templateWidth,
    height: templateHeight,
  },
): LayoutRect | null {
  const width = Math.max(
    MIN_QR_DECORATION_SIZE,
    Math.round(Math.min(templateWidth, templateHeight) * 0.22),
  )
  const height = Math.max(
    MIN_QR_DECORATION_HEIGHT,
    Math.round(width * QR_DECORATION_HEIGHT_SCALE),
  )
  if (width > contentBounds.width || height > contentBounds.height) return null

  const defaultRect = {
    width,
    height,
    x: Math.max(
      contentBounds.x,
      contentBounds.x + contentBounds.width - width - DEFAULT_DECORATION_INSET_PX,
    ),
    y: Math.min(
      contentBounds.y + DEFAULT_DECORATION_INSET_PX,
      contentBounds.y + contentBounds.height - height,
    ),
  }
  const safeZoneRects = greetingCardSafeZonesToTemplateRects(safeZones, {
    width: templateWidth,
    height: templateHeight,
  })
  const overlapsSafeZone = (x: number, y: number) => greetingCardTransformOverlapsSafeZones({
    x,
    y,
    width,
    height,
    rotationDeg: 0,
    zIndex: 0,
  }, safeZoneRects)
  const overlapsExistingDecoration = (x: number, y: number) => existingDecorations.some((decoration) =>
    greetingCardRectsOverlap(
      { x, y, width, height },
      getGreetingCardRotatedBounds(decoration.transform),
    ),
  )
  const isBlocked = (x: number, y: number) => (
    overlapsSafeZone(x, y) || overlapsExistingDecoration(x, y)
  )

  if (!isBlocked(defaultRect.x, defaultRect.y)) return defaultRect

  const maxX = contentBounds.x + contentBounds.width - width
  const maxY = contentBounds.y + contentBounds.height - height
  const step = Math.max(48, Math.min(width, height) / 2)

  for (let y = contentBounds.y + DEFAULT_DECORATION_INSET_PX; y <= maxY; y += step) {
    for (let x = maxX; x >= contentBounds.x; x -= step) {
      const candidateX = Math.max(contentBounds.x, Math.min(Math.round(x), maxX))
      const candidateY = Math.max(contentBounds.y, Math.min(Math.round(y), maxY))
      if (!isBlocked(candidateX, candidateY)) {
        return {
          width,
          height,
          x: candidateX,
          y: candidateY,
        }
      }
    }
  }

  return null
}
